import React from 'react';

import PropTypes from 'prop-types';

import styled, { css } from 'styled-components';

import fonts from 'theme/fonts';
import media from 'theme/media';

const getTextColor = (props) => {
    switch (props.color) {
        case 'darkGray':
            return props.theme.black;
        case 'mediumGray':
            return props.theme.grey_72_lc;
        case 'lightGray':
            return props.theme.grey_40_lc;
        case 'superLightGray':
            return props.theme.super_light_base;
        case 'purple':
            return props.theme.purple_100;
        case 'purpleRgba40':
            return props.theme.purple_40_tr;
        case 'purpleRgba24':
            return props.theme.purple_24_tr;
        case 'red':
            return props.theme.pink_100;
        case 'white':
            return props.theme.white;
        case 'white_base':
            return props.theme.white_base;
        case 'whiteRgba40':
            return props.theme.white_40_tr_base;
        case 'whiteRgba64':
            return props.theme.white_72_tr_base;
        default:
            return props.theme.black;
    }
};

const getMobileSize = (props) => {
    switch (props.size) {
        case 'display1':
            return '32px';
        case 'display2':
            return '40px';
        case 'header':
            return '24px';
        case 'main':
            return '17px';
        case 'secondary':
            return '14px';
        case 'subheader':
            return '14px';
        case 'tag':
        case 'note':
            return '8px';
        default:
            return '17px';
    }
};
const getMobileLineHeight = (props) => {
    switch (props.size) {
        case 'display2':
            return '48px';
        case 'display1':
            return '40px';
        case 'header':
            return '36px';
        case 'main':
            return '24px';
        case 'secondary':
            return '20px';
        case 'subheader':
            return '20px';
        case 'tag':
        case 'note':
            return '10px';
        default:
            return '24px';
    }
};
const getDesktopSize = (props) => {
    switch (props.size) {
        case 'display1':
            return '40px';
        case 'display2':
            return '48px';
        case 'header':
            return '32px';
        case 'main':
            return '20px';
        case 'secondary':
            return '17px';
        case 'subheader':
            return '17px';
        case 'tag':
        case 'note':
            return '11px';
        default:
            return '20px';
    }
};
const getDesktopLineHeight = (props) => {
    switch (props.size) {
        case 'display1':
            return '56px';
        case 'display2':
            return '64px';
        case 'header':
            return '48px';
        case 'main':
            return '32px';
        case 'secondary':
            return '24px';
        case 'subheader':
            return '24px';
        case 'tag':
        case 'note':
            return '16px';
        default:
            return '32px';
    }
};

const getComponent = (variant) => {
    switch (variant) {
        case 'h1':
            return TextHeader1;
        case 'h2':
            return TextHeader2;
        case 'h3':
            return TextHeader3;
        case 'h4':
            return TextHeader4;
        case 'h5':
            return TextHeader5;
        case 'h6':
            return TextHeader6;
        case 'pre':
            return TextPre;
        case 'paragraph':
            return TextParagraph;
        case 'div':
            return TextDiv;
        case 'link':
            return TextLink;
        case 'button':
            return TextButton;
        case 'listItem':
            return TextListItem;
        default:
            return TextSpan;
    }
};

const getBottomIndent = (props) => {
    switch (props.indent) {
        case '4px':
            return '4px';
        case '8px':
            return '8px';
        case '16px':
            return '16px';
        case '24px':
            return '24px';
        case '32px':
            return '32px';
        case '48px':
            return '48px';
        case '64px':
            return '64px';
        case 'adaptive-8-16':
            return '8px';
        case 'adaptive-16-24':
            return '16px';
        case 'adaptive-24-32':
            return '24px';
        case 'adaptive-32-48':
            return '32px';
        default:
            return null;
    }
};
const getDesktopBottomIndent = (props) => {
    switch (props.indent) {
        case '4px':
            return '4px';
        case '8px':
            return '8px';
        case '16px':
            return '16px';
        case '24px':
            return '24px';
        case '32px':
            return '32px';
        case '48px':
            return '48px';
        case '64px':
            return '64px';
        case 'adaptive-8-16':
            return '16px';
        case 'adaptive-16-24':
            return '24px';
        case 'adaptive-24-32':
            return '32px';
        case 'adaptive-32-48':
            return '48px';
        default:
            return null;
    }
};

const CommonCss = css`
    font-family: ${fonts.regular};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-size: ${(props) => getMobileSize(props)};
    line-height: ${(props) => getMobileLineHeight(props)};
    font-variant: ${(props) => (props.smallCaps ? 'small-caps' : null)};
    letter-spacing: ${(props) => (props.size === 'subheader' || props.size === 'tag' ? '0.08em' : null)};
    text-transform: ${(props) => (props.size === 'subheader' || props.size === 'tag' ? 'uppercase' : null)};
    color: ${(props) => getTextColor(props)};
    font-weight: ${(props) => (props.size === 'tag' || props.isBold ? 'bold' : null)};
    margin-left: ${(props) => (props.leftSpacer ? '3px' : null)};
    margin-bottom: ${(props) => getBottomIndent(props)};
    white-space: ${(props) => (props.nowrap || props.ellipsis ? 'nowrap' : null)};
    overflow: ${(props) => (props.ellipsis ? 'hidden' : null)};
    text-overflow: ${(props) => (props.ellipsis ? 'ellipsis' : null)};
    max-width: ${(props) => (props.ellipsis ? '100%' : null)};
    text-decoration: ${(props) => (props.lineThrough ? 'line-through' : null)};
    text-align: ${(props) => (props.textAlign ? props.textAlign : null)};
    flex-shrink: ${(props) => (props.flexShrink ? props.flexShrink : null)};

    &:link,
    &:visited {
        color: ${(props) => getTextColor(props)};
    }

    ${media.desktop1920} {
        font-size: ${(props) => getDesktopSize(props)};
        line-height: ${(props) => getDesktopLineHeight(props)};
        margin-bottom: ${(props) => getDesktopBottomIndent(props)};
    }
`;

const TextSpan = styled.span`
    ${CommonCss};
`;
const TextDiv = styled.div`
    ${CommonCss};
`;
const TextHeader1 = styled.h1`
    ${CommonCss};
`;
const TextHeader2 = styled.h2`
    ${CommonCss};
`;
const TextHeader3 = styled.h3`
    ${CommonCss};
`;
const TextHeader4 = styled.h4`
    ${CommonCss};
`;
const TextHeader5 = styled.h5`
    ${CommonCss};
`;
const TextHeader6 = styled.h6`
    ${CommonCss};
`;
const TextPre = styled.pre`
    ${CommonCss};
`;
const TextParagraph = styled.p`
    ${CommonCss};
`;
const TextLink = styled.a`
    ${CommonCss};
`;
const TextButton = styled.button`
    ${CommonCss};
    width: auto;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    text-align: left;

    &:hover,
    &:focus {
        background: none;
    }
`;
const TextListItem = styled.li`
    ${CommonCss};
`;

export const Text = (props) => {
    const Wrapper = getComponent(props.variant);
    return (
        <Wrapper {...props} data-testid={props.dataTestId}>
            {props.children}
        </Wrapper>
    );
};

Text.propTypes = {
    variant: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'paragraph', 'div', 'span', 'link', 'button', 'listItem']),
    color: PropTypes.oneOf([
        'darkGray',
        'mediumGray',
        'lightGray',
        'superLightGray',
        'purple',
        'purpleRgba40',
        'purpleRgba24',
        'red',
        'white',
        'white_base',
        'whiteRgba40',
        'whiteRgba64',
    ]),
    size: PropTypes.oneOf(['display1', 'display2', 'header', 'main', 'secondary', 'subheader', 'tag', 'note']),
    smallCaps: PropTypes.bool,
    nowrap: PropTypes.bool,
    leftSpacer: PropTypes.bool,
    ellipsis: PropTypes.bool,
    href: PropTypes.string,
    onClick: PropTypes.func,
    indent: PropTypes.oneOf(['4px', '8px', '16px', '24px', '32px', '48px', '64px', 'adaptive-8-16', 'adaptive-16-24', 'adaptive-24-32', 'adaptive-32-48']),
    lineThrough: PropTypes.bool,
    textAlign: PropTypes.string,
    flexShrink: PropTypes.string,
    isBold: PropTypes.bool,
};
